export type WellnessKind = 'yoga' | 'gym' | 'spa'

export interface WellnessService {
  readonly name: string
  readonly duration: string
  readonly price: number
  /** What the same thing costs in the US, when the gap is the reason to book. */
  readonly usPrice?: number
}

import type { CityId } from './travel-data'

export interface WellnessVenue {
  readonly slug: string
  readonly name: string
  readonly kind: WellnessKind
  /** Which corridor it is in, for the city filter. */
  readonly cityId: CityId
  /** The neighbourhood line shown to the user. */
  readonly city: string
  readonly detail: string
  readonly score: string
  readonly verdict: string
  readonly reviews: number
  readonly languages: string
  readonly blurb: string
  readonly services: readonly WellnessService[]
  readonly slots: readonly string[]
  /** Path in the asset bucket. */
  readonly image: string
}

export const KINDS: ReadonlyArray<{
  readonly id: WellnessKind
  readonly icon: string
  readonly label: string
  readonly count: string
}> = [
  { id: 'yoga', icon: 'self_improvement', label: 'Yoga', count: '310 classes' },
  { id: 'gym', icon: 'exercise', label: 'Gyms', count: '86 nearby' },
  { id: 'spa', icon: 'spa', label: 'Spa', count: '74 nearby' },
]

export const VENUES: readonly WellnessVenue[] = [
  {
    slug: 'estudio-aurora',
    cityId: 'cdmx',
    image: 'wellness/estudio-aurora.webp',
    name: 'Estudio Aurora',
    kind: 'yoga',
    city: 'Roma Norte, CDMX',
    detail: 'Roma Norte · classes from six in the morning',
    score: '9.1',
    verdict: 'Excellent',
    reviews: 402,
    languages: 'Spanish and English',
    blurb:
      'A studio on a quiet corner of Roma Norte with classes from six in the morning. Mats and towels are included, so you can walk in with nothing.',
    services: [
      { name: 'Vinyasa class', duration: '60 min', price: 18 },
      { name: 'Ten-class pack', duration: 'Valid 3 months', price: 150 },
      { name: 'Private session', duration: '60 min', price: 46 },
    ],
    slots: ['Today · 07:00', 'Today · 19:00', 'Tomorrow · 06:30', 'Tomorrow · 18:00'],
  },
  {
    slug: 'casa-mantra',
    cityId: 'bogota',
    image: 'wellness/casa-mantra.webp',
    name: 'Casa Mantra',
    kind: 'yoga',
    city: 'Chapinero, Bogotá',
    detail: 'Chapinero · hatha, vinyasa and breathwork',
    score: '9.0',
    verdict: 'Excellent',
    reviews: 268,
    languages: 'Spanish',
    blurb:
      'A converted house in Chapinero with a room that looks onto the hills. Classes stay small, and the teachers correct you rather than shout at you.',
    services: [
      { name: 'Drop-in class', duration: '75 min', price: 12 },
      { name: 'Week unlimited', duration: '7 days', price: 34 },
      { name: 'Breathwork session', duration: '45 min', price: 16 },
    ],
    slots: ['Today · 06:30', 'Today · 18:30', 'Tomorrow · 07:30'],
  },
  {
    slug: 'gimnasio-reforma-92',
    cityId: 'cdmx',
    image: 'wellness/gimnasio-reforma-92.webp',
    name: 'Gimnasio Reforma 92',
    kind: 'gym',
    city: 'Cuauhtémoc, CDMX',
    detail: 'Open 05:00–23:00 · weights and cardio',
    score: '8.8',
    verdict: 'Very good',
    reviews: 928,
    languages: 'Spanish',
    blurb:
      'A large floor two blocks from Reforma, open from five in the morning. A day pass gets you the weight room, the machines and the showers.',
    services: [
      { name: 'Day pass', duration: 'One day', price: 6 },
      { name: 'Week pass', duration: '7 days', price: 19 },
      { name: 'Month · no contract', duration: '30 days', price: 34 },
    ],
    slots: ['Today · any time', 'Tomorrow · any time'],
  },
  {
    slug: 'academia-corpo-livre',
    cityId: 'sao-paulo',
    image: 'wellness/academia-corpo-livre.webp',
    name: 'Academia Corpo Livre',
    kind: 'gym',
    city: 'Pinheiros, São Paulo',
    detail: 'Weights, pool and classes included',
    score: '9.0',
    verdict: 'Excellent',
    reviews: 514,
    languages: 'Portuguese',
    blurb:
      'A neighbourhood gym in Pinheiros with a pool and classes at no extra cost. The day pass includes everything on the timetable.',
    services: [
      { name: 'Day pass', duration: 'One day', price: 8 },
      { name: 'Week pass', duration: '7 days', price: 24 },
      { name: 'Month · no contract', duration: '30 days', price: 41 },
    ],
    slots: ['Today · any time', 'Tomorrow · any time'],
  },
  {
    slug: 'centro-equipetrol',
    cityId: 'santa-cruz',
    image: 'wellness/centro-equipetrol.webp',
    name: 'Centro Equipetrol',
    kind: 'gym',
    city: 'Equipetrol, Santa Cruz',
    detail: 'Functional training · coached sessions',
    score: '8.9',
    verdict: 'Very good',
    reviews: 203,
    languages: 'Spanish',
    blurb:
      'Coached functional sessions on the hour in Equipetrol. Drop in for a single class or take the week if you are visiting family for a while.',
    services: [
      { name: 'Drop-in session', duration: '60 min', price: 7 },
      { name: 'Week pass', duration: '7 days', price: 21 },
      { name: 'Month · no contract', duration: '30 days', price: 38 },
    ],
    slots: ['Today · 18:00', 'Today · 19:00', 'Tomorrow · 07:00'],
  },
  {
    slug: 'bano-condesa',
    cityId: 'cdmx',
    image: 'wellness/bano-condesa.webp',
    name: 'Baño Condesa',
    kind: 'spa',
    city: 'Condesa, CDMX',
    detail: 'Steam, sauna and massage · walk-ins welcome',
    score: '9.2',
    verdict: 'Excellent',
    reviews: 376,
    languages: 'Spanish and English',
    blurb:
      'Steam room, sauna and cold plunge in a basement off Avenida Amsterdam. Two hours of circuit, or add a massage on the way out.',
    services: [
      { name: 'Thermal circuit', duration: '2 hours', price: 24 },
      { name: 'Circuit + massage', duration: '2h 50m', price: 58 },
      { name: 'Deep tissue massage', duration: '50 min', price: 38 },
    ],
    slots: ['Today · 15:00', 'Today · 19:30', 'Tomorrow · 11:00'],
  },
  {
    slug: 'termas-pinheiros',
    cityId: 'sao-paulo',
    image: 'wellness/termas-pinheiros.webp',
    name: 'Termas Pinheiros',
    kind: 'spa',
    city: 'Pinheiros, São Paulo',
    detail: 'Pools, sauna and treatments',
    score: '9.3',
    verdict: 'Exceptional',
    reviews: 441,
    languages: 'Portuguese and English',
    blurb:
      'Warm pools and a dry sauna on the top floor, with the city below. The day pass runs from opening to close, so you can come back after lunch.',
    services: [
      { name: 'Day pass', duration: 'All day', price: 29 },
      { name: 'Pass + massage', duration: 'All day', price: 71 },
      { name: 'Facial treatment', duration: '45 min', price: 44 },
    ],
    slots: ['Today · 12:00', 'Today · 17:00', 'Tomorrow · 10:00'],
  },
]

/** Wellness and health pays 5% back, not the flat 3% travel used to promise. */
export const WELLNESS_CASHBACK = 0.05

/** Cheapest service, which is what the list and the filters price on. */
export function entryPrice(venue: WellnessVenue): number {
  return Math.min(...venue.services.map((service) => service.price))
}

export function findVenue(slug: string): WellnessVenue | undefined {
  return VENUES.find((venue) => venue.slug === slug)
}
